import React, { useEffect, useRef, useState } from 'react';
import { Box, Fade } from '@material-ui/core';
import { wait } from 'globals/utils';
import { registerEmploymentBasicStep as registerLocales } from 'globals/locales/registration';
import defaultLocales from 'globals/locales/default';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsSentenceSelect from 'ui-elements/form-elements/FsSentenceSelect';
import { showTransactionDetailsOptionList } from 'globals/locales/select-options';
import { SelectWrapper } from './FsRegisterEmploymentBasicsForm.style-elements';
import { Sentence } from '../FsRegisterEmploymentDetailsForm/FsRegisterEmploymentDetailsForm.style-elements';

export interface FsRegisterEmploymentBasicsFormDataProps {
  showTransactionDetails: string;
}

interface OnGoNextProps {
  showTransactionDetails: boolean;
}

interface FsRegisterEmploymentBasicsFormProps
  extends FsRegisterEmploymentBasicsFormDataProps {
  onClose?: () => void;
  onGoNext?: (props: OnGoNextProps) => void;
}

const FsRegisterEmploymentBasicsForm: React.FC<FsRegisterEmploymentBasicsFormProps> = ({
  onClose,
  onGoNext,
  ...defaults
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState<string>(
    defaults.showTransactionDetails
  );
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const fields = [
    {
      isSelect: true,
      options: showTransactionDetailsOptionList,
      required: true,
      name: 'show-transaction-details',
      value: showTransactionDetails,
      label: defaultLocales.chooseOption.toLowerCase(),
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setShowTransactionDetails(e.target?.value);
      },
      prefix: registerLocales.showTransactionDetailsPrefix,
      suffix: registerLocales.showTransactionDetailsSuffix,
    },
  ];

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      showTransactionDetails: showTransactionDetails === 'true',
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    if (onGoNext)
      onGoNext({ showTransactionDetails: showTransactionDetails === 'true' });
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    setIsFormValid(isDefaultFormValid);
  }, [showTransactionDetails]);

  return (
    <Box>
      <form onSubmit={handleSubmit} ref={formEl}>
        {fields.map(
          ({ name, isSelect, prefix, suffix, options, ...otherProps }) => (
            <FsFormFullWidthContainer key={name}>
              {isSelect && options && (
                <Fade in timeout={300}>
                  <Sentence component="div">
                    {prefix && `${prefix} `}
                    <SelectWrapper>
                      <FsSentenceSelect
                        {...{ ...otherProps, name, options }}
                        optionCase="lc"
                      />
                    </SelectWrapper>
                    {suffix && ` ${suffix}`}
                  </Sentence>
                </Fade>
              )}
            </FsFormFullWidthContainer>
          )
        )}
        <Box mt={10}>
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            disabled={!isFormValid}
            endIcon={<FsNextArrowIcon />}
            width={160}
            spaceBetween
          >
            {defaultLocales.continue}
          </FsPendingButton>
        </Box>
      </form>
    </Box>
  );
};

export default FsRegisterEmploymentBasicsForm;
